// Singleton-Store fuer alle aktiven Alerts (critical/problem/warning/info).
// Aggregiert Domain-Stores + events-Tabelle. Speist TopBar-Counter + Popover,
// die Meldungen-Seite und das untere Panel. Kritisches ist seit 13.09.2026
// mit drin (vorher nur im Dashboard-Banner, s. fasseZustaende) -- es ist
// nicht quittierbar und wird von ackAlert/ackAllVisible uebergangen.
// Dashboard liest getAlertsVisible() statt lokaler Berechnung.
//
// Hier lebt nur, was ZUSTAND hat: Laden, Acks, Protokoll-Anbindung und die
// aus den Domain-Stores abgeleiteten Meldungen.
//
// Live-Weg (seit 02.11.2026): das Protokoll-Topic `meldung`
// (protokoll/meldungen.svelte.ts). Jede gespeicherte Änderung -- neue und
// aktualisierte Zeile, Quittierung, Änderung an alert_acks -- lädt gebündelt
// (200 ms) die Ereignisse bzw. die Quittierungen neu; eine Lücke lädt beides.
// Der 30-s-Abgleich und der WS-Kanal „events" entfallen; das Netz darunter
// ist der Revalidator (Fokuswechsel, Wiederverbinden). Was eine Meldung IST und wie
// aus Ereignissen Zustaende werden, steht runes-frei und getestet in
// meldungs-logik.ts.

import { registerRevalidator } from "./revalidate.svelte";
import { getWan } from "./wan-summary.svelte";
import { wanMeldungen } from "./netzwerk/netz";
import { getHostsSummary } from "./hosts-summary.svelte";
import { getSummary as getSecuritySummary } from "./security.svelte";
import { getDnsHealth } from "./dns-health.svelte";
import { getKlimaSummary } from "./klima-summary.svelte";
import { getTasksSummary, refreshTasksSummary } from "./tasks-summary.svelte";
import { isEntityDisabled } from "./entity-disables.svelte";
import { getJSON, postJSON } from "./api";
import {
  alertFingerprint,
  taskFailAlert,
  BANNER_TYPE_EXCLUDE,
  BANNER_TYPE_EXCLUDE_CSV,
  fasseZustaende,
  quittierbar,
  severityRang,
  type Alert,
} from "./meldungs-logik";
import type { AppEvent } from "./types";
import { betrifftListe, istAckAenderung, meldungRefetch } from "./protokoll/meldungen.svelte";

// ── Event-Alerts aus der events-Tabelle ───────────────────────────────────────

/** Wie weit der Rueckblick auf EINMALIGE Ereignisse reicht. Gilt nur fuer
 *  die info-Sorte (neues Geraet im Netz, Entwarnungen): die beschreiben
 *  einen Moment, und ein Moment von vor zwei Wochen ist keine offene
 *  Meldung mehr. */
const EVENT_INFO_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

let _eventAlerts = $state<AppEvent[]>([]);

// loadEventAlerts stellt ZWEI verschiedene Fragen und braucht deshalb
// zwei Abfragen:
//
//   1. "Was ist offen?" -- critical/warn/error, unacked, OHNE Zeitfenster.
//      Bis 23.08.2026 hing auch diese Frage am 7-Tage-Fenster, und das
//      war ein stiller Datenverlust: ein device_offline ist eine FLANKE,
//      die niemand nachbumpt. Blieb ein Geraet laenger als eine Woche
//      weg, rutschte seine Warnung aus dem Fenster und die Meldung war
//      verschwunden -- das Geraet aber immer noch. Offene Warnungen
//      duerfen nicht altern. Ihre Zahl ist durch das Coalescing im
//      Backend (eine Zeile je domain/type/entity) ohnehin gedeckelt.
//
//   2. "Was ist zuletzt passiert?" -- info, unacked, letzte 7 Tage.
//      Das sind die einmaligen Hinweise UND die Entwarnungen, die
//      fasseZustaende braucht, um eine Stoerung stillzustellen.
//
// Der zweite Grund fuer die Trennung: info-Ereignisse werden nie
// quittiert und lagen dadurch dauerhaft im gemeinsamen 200er-Limit. Eine
// flatterige Woche konnte damit echte offene Warnungen aus dem Ergebnis
// draengen.
async function loadEventAlerts(): Promise<void> {
  const seit = new Date(Date.now() - EVENT_INFO_MAX_AGE_MS).toISOString();
  const ausgeschlossen = encodeURIComponent(BANNER_TYPE_EXCLUDE_CSV);
  try {
    const [offen, hinweise] = await Promise.all([
      getJSON<AppEvent[]>(
        `/v2-api/events?severity=critical,warn,error&unacked=true&type_exclude=${ausgeschlossen}&limit=200`,
      ),
      getJSON<AppEvent[]>(
        `/v2-api/events?severity=info&unacked=true&type_exclude=${ausgeschlossen}` +
          `&since=${encodeURIComponent(seit)}&limit=200`,
      ),
    ]);
    _eventAlerts = [...offen, ...hinweise];
  } catch {
    // ignore
  }
}

// ── Ack-System ────────────────────────────────────────────────────────────────

type AckedAlert = { alert_key: string; fingerprint: string; acked_at: string };

let _acks = $state<AckedAlert[]>([]);
let _acksLoaded = $state(false);

async function loadAcks(): Promise<void> {
  try {
    _acks = await getJSON<AckedAlert[]>("/v2-api/alert-acks");
  } catch {
    _acks = [];
  } finally {
    _acksLoaded = true;
  }
}


function isAcked(a: Alert): boolean {
  const fp = alertFingerprint(a);
  return _acks.some((ack) => ack.alert_key === a.id && ack.fingerprint === fp);
}

// ── Synthesized Alerts aus Domain-Stores ─────────────────────────────────────

const _alerts = $derived.by<Alert[]>(() => {
  const out: Alert[] = [];
  const wan = getWan();
  const hosts = getHostsSummary();
  const security = getSecuritySummary();
  const dns = getDnsHealth();
  const klima = getKlimaSummary();
  const tasks = getTasksSummary();

  // Internet je Router: der hinter forge („wan", Probe), jeder andere
  // („wan:<kennung>", was sein Agent meldet) -- lib/netzwerk/netz.ts.
  out.push(...wanMeldungen(wan));
  if (hosts && hosts.hosts_offline > 0) {
    const a = hosts.top_anomalies?.[0];
    out.push({
      id: "hosts", severity: "problem",
      title: hosts.hosts_offline === 1 ? "1 Host offline" : `${hosts.hosts_offline} Hosts offline`,
      detail: a ? `${a.host_id} · letzter Ping nicht erreicht` : undefined,
      href: "/infra/cluster",
    });
  }
  // Ziel ist /security (die Sicherheits-Uebersicht). Bis 23.08.2026 stand
  // hier "/sicherheit/security" -- eine Route, die es nie gab und die
  // auch kein Redirect auffing: jeder Klick auf eine Sicherheits-Meldung
  // landete im 404.
  if (security?.currently_failed && security.currently_failed > 0) {
    out.push({
      id: "sec-attempts", severity: "warning",
      title: "Login-Versuche aktiv",
      detail: `${security.currently_failed} ${security.currently_failed === 1 ? "IP versucht" : "IPs versuchen"} Login`,
      href: "/security",
    });
  }
  // Aktive Bans sind KEINE Meldung aus dem Zustand mehr. Ein Ban heisst,
  // die Sperre hat gegriffen -- bis 04.10.2026 stand deshalb dauernd
  // "2 aktive Bans" als Warnung da. Jetzt kommt je Ban ein Hinweis aus dem
  // Ereignis selbst (fail2ban/ban, severity info): sehen, pruefen, als
  // gelesen abhaken. Mit eventId, also EIN "gelesen" fuer Meldungen und
  // Sicherheits-Seite.
  if (dns && dns.health === "down") {
    out.push({
      id: "dns", severity: "warning",
      title: "DNS nicht erreichbar",
      detail: `Resolver ${dns.server} · OpenWrt-Fallback aktiv`,
      href: "/sicherheit/dns",
    });
  }
  if (klima) {
    for (const s of klima.sensors) {
      if (s.disabled || isEntityDisabled(s.domain, s.sensor_id)) continue;
      if (s.status === "problem" || s.status === "warning") {
        out.push({
          id: `klima-${s.domain}-${s.sensor_id}`,
          severity: s.status as "warning" | "problem",
          title: `${s.sensor_id} · ${s.domain}`,
          detail: s.reason ?? undefined,
          ts: s.ts ?? undefined,
          href: "/zuhause/sensoren",
        });
      }
    }
  }
  // Fehlgeschlagene Tasks: eine Meldung je Task, aus dem JETZT-Zustand.
  //
  // ⚠️ Hier stand bis 23.08.2026 `failed_last_24h > 0` — ein Zähler über
  // 24 Stunden. Damit klebte die Warnung einen ganzen Tag am Dreieck,
  // obwohl der Task längst wieder grün lief und der Server das Ereignis
  // selbst geschlossen hatte (acked_by="auto:…"). Ein Zähler ist keine
  // Zustandsaussage. `currently_failing` listet die Tasks, deren
  // LETZTES Lauf-Ereignis ein offener Fehlschlag ist — läuft einer
  // wieder durch, fällt er von selbst raus.
  for (const f of tasks?.currently_failing ?? []) out.push(taskFailAlert(f));
  // Ereignis-Meldungen: Zustands-Paare eingedampft (siehe fasseZustaende).
  out.push(...fasseZustaende(_eventAlerts));
  out.sort((a, b) => severityRang(a.severity) - severityRang(b.severity));
  return out;
});

const _alertsVisible = $derived(
  _acksLoaded ? _alerts.filter((a) => !isAcked(a)) : [],
);

// ── Public API ────────────────────────────────────────────────────────────────

export function getAlertsVisible(): Alert[] {
  return _alertsVisible;
}

export function getAlertsCount(): number {
  return _alertsVisible.length;
}

/** Die dringendste offene Stufe, oder null wenn nichts offen ist.
 *
 *  Die Liste ist nach Rang sortiert (problem < warning < info), der erste
 *  Eintrag ist also der schlimmste. Wer ein Signal einfaerbt, muss diese
 *  Frage stellen -- nicht "gibt es ueberhaupt etwas?": das Dreieck in der
 *  Leiste trug bis 28.08.2026 einen fest roten Punkt und meldete damit
 *  einen Fehler, waehrend die einzige offene Sache eine Warnung war. */
export function getAlertsTopSeverity(): Alert["severity"] | null {
  return _alertsVisible[0]?.severity ?? null;
}

/** nachfassen laedt die Domain-Summaries neu, aus denen quittierte
 *  Meldungen stammen.
 *
 *  Warum das noetig ist: ein Ack schreibt nur `acked_at` in die
 *  events-Zeile und loest KEIN neues Ereignis aus -- der WS-Kanal
 *  schweigt also. Meldungen, die aus einer Summary gebaut werden
 *  (heute die Task-Fehlschlaege), stehen zudem nicht in `_eventAlerts`
 *  und werden vom optimistischen Filter dort nicht erwischt.
 *
 *  `tasks-summary` hat obendrein gar keinen Poll: sie laedt nur bei
 *  einem WS-Ereignis ihrer Domain oder beim Revalidieren (Fokus-
 *  wechsel). Ohne dieses Nachfassen blieb die Zeile nach "Alle gelesen"
 *  darum praktisch unbegrenzt stehen -- bis Tim das Fenster wechselte
 *  oder zufaellig irgendein anderer Task fertig wurde (01.09.2026).
 *
 *  Seit 02.11.2026 meldet der Server auch das Quittieren (Protokoll-Topic
 *  `meldung`, dazu ein Rahmen auf "events"); das Nachfassen bleibt, damit die
 *  Karte sofort umspringt und nicht erst, wenn der Rahmen da ist.
 *
 *  Wer eine weitere Summary-Quelle an die Meldungen haengt, traegt sie
 *  hier ein. */
async function nachfassen(items: Alert[]): Promise<void> {
  if (items.some((a) => a.domain === "tasks" && a.eventId != null)) {
    await refreshTasksSummary();
  }
}

export async function ackAlert(a: Alert): Promise<void> {
  // Kritisches klaert nur die Ursache -- der Server antwortet 403, und
  // das optimistische Entfernen liesse die Zeile bis zum naechsten Poll
  // verschwinden, als waere die Sache erledigt.
  if (!quittierbar(a)) return;
  if (a.eventId != null) {
    const id = a.eventId;
    _eventAlerts = _eventAlerts.filter((e) => e.id !== id);
    await postJSON(`/v2-api/events/${id}/ack`, {}).catch(() => { void loadEventAlerts(); });
    await nachfassen([a]);
    return;
  }
  const fp = alertFingerprint(a);
  const at = new Date().toISOString();
  _acks = [..._acks.filter((x) => x.alert_key !== a.id), { alert_key: a.id, fingerprint: fp, acked_at: at }];
  await postJSON(`/v2-api/alert-acks`, { alert_key: a.id, fingerprint: fp }).catch(() => { void loadAcks(); });
}

export async function ackAllVisible(): Promise<void> {
  const items = _alertsVisible.filter(quittierbar);
  if (!items.length) return;
  const eventIds = items.filter((a) => a.eventId != null).map((a) => a.eventId as number);
  const storeAcks = items.filter((a) => a.eventId == null).map((a) => ({
    alert_key: a.id, fingerprint: alertFingerprint(a),
  }));
  if (eventIds.length > 0) {
    const drop = new Set(eventIds);
    _eventAlerts = _eventAlerts.filter((e) => e.id == null || !drop.has(e.id));
  }
  if (storeAcks.length > 0) {
    const at = new Date().toISOString();
    const newAcks = storeAcks.map((s) => ({ ...s, acked_at: at }));
    const keys = new Set(newAcks.map((x) => x.alert_key));
    _acks = [..._acks.filter((x) => !keys.has(x.alert_key)), ...newAcks];
  }
  await Promise.all([
    ...eventIds.map((id) => postJSON(`/v2-api/events/${id}/ack`, {}).catch(() => undefined)),
    ...storeAcks.map((s) => postJSON(`/v2-api/alert-acks`, s).catch(() => undefined)),
  ]);
  await Promise.all([loadEventAlerts(), loadAcks(), nachfassen(items)]);
}

// ── Stream-Lifecycle ──────────────────────────────────────────────────────────

let _unsubMeldung: Array<() => void> = [];
let _started = false;

async function refresh(): Promise<void> {
  await Promise.all([loadEventAlerts(), loadAcks()]);
}

export function startAlertsStream(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(refresh);
  // Erst hören, dann laden: was zwischen Abfrage und Abo geschähe, ginge sonst
  // verloren.
  // Eine Änderung an alert_acks betrifft nur die Quittierungen, alles andere
  // nur die Ereignisse, eine Lücke beides (zwei Abonnements, ein Fenster je
  // Quelle).
  _unsubMeldung = [
    meldungRefetch(loadEventAlerts, { ms: 200, filter: betrifftListe({ ohneTypen: BANNER_TYPE_EXCLUDE }) }),
    meldungRefetch(loadAcks, { ms: 200, filter: istAckAenderung }),
  ];
  void refresh();
}

export function stopAlertsStream(): void {
  _started = false;
  for (const ab of _unsubMeldung) ab();
  _unsubMeldung = [];
  _acks = [];
  _acksLoaded = false;
}
